// Typed client for a court's PHYSICAL state (#271) — whether the court can be
// used at all, as distinct from whether it is offered (`enabledCourts`).
//
// The state lives in `courtMeta[<court>].outOfService` inside the club's
// `booking_config_v1` document, and on the API backend the server OWNS that
// subtree: `PUT /kv/booking_config_v1` drops whatever the body says about it.
// So this is the only way to change it there — writing the config blob from the
// browser will not work, by design, because the settings editor's save would
// otherwise reopen a court somebody closed while the dialog was open.
//
// In memory there is no server to own anything, so the same operation
// is a read-modify-write of the blob. That is what the deployed browser runs
// today, and it keeps the control working there rather than failing silently.
import { Storage } from '../storage';
import { apiFetch } from './http';

const KEY = 'booking_config_v1';

/** The state as the server returns it. `since` is the server's clock. */
export interface CourtOutOfService {
  reason: string | null;
  since: number | null;
}

export interface CourtStateResult {
  court: number;
  outOfService: CourtOutOfService | null;
  /** The whole config after the write — the caller refreshes its copy from it. */
  config: any;
}

/** True when the state is owned by the server rather than by this browser. */
export const courtStateIsServerAuthoritative = () => Storage.backend === 'http';

async function readConfig(): Promise<any> {
  const r = await Storage.get(KEY);
  return r?.value ? JSON.parse(r.value) : {};
}

/** The legacy path: edit the subtree in the blob ourselves. */
async function writeLocally(
  court: number,
  state: CourtOutOfService | null,
): Promise<CourtStateResult> {
  const config = await readConfig();
  const courtMeta = { ...(config.courtMeta || {}) };
  const entry = { ...(courtMeta[court] || courtMeta[String(court)] || {}) };
  if (state) entry.outOfService = state;
  else delete entry.outOfService;
  // One assignment is enough: a plain object has ONE key here — `courtMeta[3]`
  // and `courtMeta['3']` are the same property — so this overwrites whichever
  // form an older document used. (Deleting «the other one» afterwards deletes
  // this one, which is exactly what it did until the tests said so.)
  courtMeta[String(court)] = entry;
  const next = { ...config, courtMeta };
  // Storage-first: the write lands before any caller updates its state.
  await Storage.set(KEY, JSON.stringify(next));
  return { court, outOfService: state, config: next };
}

async function command(court: number, method: 'POST' | 'DELETE', body?: unknown) {
  const res = await apiFetch(`/courts/${court}/out-of-service`, {
    method,
    ...(body === undefined
      ? {}
      : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  });
  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(
      res.status === 403
        ? 'Du har ikke tilgang til å endre banestatus.'
        : detail?.error || `Kunne ikke endre banestatus (${res.status})`,
    );
  }
  return (await res.json()) as CourtStateResult;
}

/** Mark a court out of service. Idempotent; re-stating the note keeps `since`. */
export async function setCourtOutOfService(
  court: number,
  reason: string | null,
): Promise<CourtStateResult> {
  if (courtStateIsServerAuthoritative()) return command(court, 'POST', { reason });
  return writeLocally(court, { reason: reason?.trim() || null, since: Date.now() });
}

/** Put a court back into service. Idempotent. */
export async function clearCourtOutOfService(court: number): Promise<CourtStateResult> {
  if (courtStateIsServerAuthoritative()) return command(court, 'DELETE');
  return writeLocally(court, null);
}
